import {
  ServiceMetricTypes,
  SICallTypes,
  SICallTypesDisplayName,
  SICallTypesDatabaseCode,
  SICallTypesColorCode,
} from '../types/service-insight-types';
import { Injectable } from '@angular/core';
import { FiltersStore } from 'src/app/core/stores/filters.store';

export interface CategoryConfig {
  readonly key: string;
  readonly label: string;
  readonly color: string;
}

export type ChartUnit = 'calls' | 'services' | 'findings';

export interface ChartConfig {
  readonly categories: ReadonlyArray<CategoryConfig>;
  readonly unit: ChartUnit;
}

/**
 * Category order configuration for service call distribution chart.
 * Config applicable for non-Pest divisions
 */
const CATEGORY_ORDER: ReadonlyArray<CategoryConfig> = [
  { key: SICallTypes.ExtraServiceVisit,   label: SICallTypesDisplayName[SICallTypes.ExtraServiceVisit],   color: '#006BD3' },
  { key: SICallTypes.RegularServiceCalls, label: SICallTypesDisplayName[SICallTypes.RegularServiceCalls], color: '#C2DAF2' },
  { key: SICallTypes.ProgramReviews,      label: SICallTypesDisplayName[SICallTypes.ProgramReviews],      color: '#164B6E' },
  { key: SICallTypes.Audit,               label: SICallTypesDisplayName[SICallTypes.Audit],               color: '#164B6E' },
  { key: SICallTypes.Installations,       label: SICallTypesDisplayName[SICallTypes.Installations],       color: '#4FC2B0' },
  { key: SICallTypes.TrainingSessions,    label: SICallTypesDisplayName[SICallTypes.TrainingSessions],    color: '#5B6065' },
  { key: SICallTypes.QuickCalls,          label: SICallTypesDisplayName[SICallTypes.QuickCalls],          color: '#EBA68F' },
];

/**
 * Pest specific category order configuration, one for each service metric type.
 *
 * Unlike the non-Pest CATEGORY_ORDER (keyed by SICallTypes enum, since ServiceInsightsComponent
 * translates DB code -> enum before feeding the chart), the Pest datasets are fed straight from the
 * raw by-type API payload. So the `key` here must equal the API `callReason` — i.e. the DB code for
 * Services/Findings and the raw reason string for Calls — mirroring the donut chart's config.types.
 */
const PEST_SERVICES_CATEGORY_ORDER: ReadonlyArray<CategoryConfig> = [
    { key: SICallTypesDatabaseCode[SICallTypes.ContractedCleanOut], label: SICallTypesDisplayName[SICallTypes.ContractedCleanOut], color: "#006BD3" },
    { key: SICallTypesDatabaseCode[SICallTypes.ContractedRegular],  label: SICallTypesDisplayName[SICallTypes.ContractedRegular],  color: "#00376E" },
    { key: SICallTypesDatabaseCode[SICallTypes.ExtraOneShot],       label: SICallTypesDisplayName[SICallTypes.ExtraOneShot],       color: "#C2DAF2" },
    { key: SICallTypesDatabaseCode[SICallTypes.ExtraFollowUp],      label: SICallTypesDisplayName[SICallTypes.ExtraFollowUp],      color: "#00BF60" },
];

const PEST_FINDINGS_CATEGORY_ORDER: ReadonlyArray<CategoryConfig> = [
    { key: SICallTypesDatabaseCode[SICallTypes.Pest],       label: SICallTypesDisplayName[SICallTypes.Pest],       color: "#006BD3" },
    { key: SICallTypesDatabaseCode[SICallTypes.Sanitation], label: SICallTypesDisplayName[SICallTypes.Sanitation], color: "#C2DAF2" },
    { key: SICallTypesDatabaseCode[SICallTypes.Structural], label: SICallTypesDisplayName[SICallTypes.Structural], color: "#00376E" },
    { key: SICallTypesDatabaseCode[SICallTypes.Prep],       label: SICallTypesDisplayName[SICallTypes.Prep],       color: "#00BF60" },
];

// Mirrors the donut chart's Pest_CallsByReason config. Keys are the raw callReason
// values from the API (no SICallTypes enum members exist for most of these), so plain
// strings are used for both key and label except where a type is available (Pest ESR).
// TODO: Need to asses impact and replace with proper SICallTypes enum members and adjustdownstream usage
const PEST_CALLS_CATEGORY_ORDER: ReadonlyArray<CategoryConfig> = [
  { key: 'PEST ESR', label: SICallTypesDisplayName[SICallTypes.ExtraServiceRequest], color: '#006BD3' },
  { key: 'NON-PEST ESR', label: 'Non-Pest ESR', color: '#164B6E' },
  { key: 'INFORMATION', label: 'Information', color: '#4FC2B0' },
  { key: 'NO SHOW/NO RESPONSE', label: 'No Show/No Response', color: '#C2DAF2' },
  { key: 'PLEASE CALL', label: 'Please Call', color: '#5B6065' },
  { key: 'ALL OTHER', label: 'All Other', color: '#00BF60' },
];

@Injectable({ providedIn: 'root' })
export class ServiceCallDistributionChartConfigResolver {
  constructor(private _filterStore: FiltersStore) {}

  resolve(serviceMetricType: ServiceMetricTypes): ChartConfig {
    const filters = this._filterStore.selectedFiltersValue;
    return filters?.division === 'Pest'
      ? this.resolveForPestMetricType(serviceMetricType)
      : { categories: CATEGORY_ORDER, unit: 'calls' };
  }

  resolveForPestMetricType(serviceMetricType: ServiceMetricTypes): ChartConfig {
    switch (serviceMetricType) {
      case ServiceMetricTypes.Services:      return { categories: PEST_SERVICES_CATEGORY_ORDER, unit: 'services' };
      case ServiceMetricTypes.Findings:      return { categories: PEST_FINDINGS_CATEGORY_ORDER, unit: 'findings' };
      case ServiceMetricTypes.CallsByReason: return { categories: PEST_CALLS_CATEGORY_ORDER,    unit: 'calls'    };
      default:                               return { categories: [],                            unit: 'calls'    };
    }
  }
}
